import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";

export interface Mentorship {
  mentorship_id: string;
  mentor_id: string;
  mentee_id: string;
  mentor_name: string;
  mentee_name: string | null;
  mentor_email: string;
  mentee_email: string | null;
  mentor_profile_picture_url: string;
  mentee_profile_picture_url: string | null;
  teen_days_engaged: string;
  teen_messages: string;
  average_response_time: string;
  scheduled_average_response_time: string;
  total_calls: string;
  avg_call_duration: string;
  last_message: string;
  last_message_date: string;
  average_rating: string;
  days_in_mentorship: string;
  last_mentor_interaction: string;
  has_open_escalation: boolean;
  days_to_churn: number;
  latest_mood_score: number | null;
  deactivated: boolean;
  unique_days_engaged_this_month: string;
  created_at: string;
}
interface MentorshipsResponse {
  mentorships: Mentorship[];
  hasMore: boolean;
}

interface UseGetMentorships {
  month: number;
  mentorId?: string;
  status?: string;
  search?: string;
  groups?: string[];
  page: number;
  perPage: number;
  sort?: string;
  sortDirection?: string;
  last7Days?: boolean;
  engagementFilter?: "engaged" | "not_engaged";
  deactivatedThisMonth?: boolean;
  isMentor?: boolean;
  timezone?: string;
}

export const useGetMentorships = (payload: UseGetMentorships) => {
  return useQuery<MentorshipsResponse>({
    queryKey: [
      "mentorships",
      payload.month,
      payload.mentorId,
      payload.status,
      payload.search,
      payload.page,
      payload.perPage,
      payload.groups,
      payload.sort,
      payload.sortDirection,
      payload.last7Days,
      payload.engagementFilter,
      payload.deactivatedThisMonth,
      payload.isMentor,
      payload.timezone,
    ],
    queryFn: async () => {
      const url = payload.isMentor
        ? "v2/mentor/mentorships"
        : "v2/admin/mentorships";

      const { data } = await fetchApi<MentorshipsResponse>({
        url: url,
        method: "GET",
        params: {
          month: payload.month === -1 ? undefined : payload.month,
          mentorId: payload.mentorId,
          status: payload.status,
          search: payload.search,
          page: payload.page,
          limit: payload.perPage,
          groups: payload.groups?.includes("all") ? undefined : payload.groups,
          sort: payload.sort,
          sortDirection: payload.sortDirection,
          last7Days: payload.last7Days,
          engagementFilter: payload.engagementFilter,
          deactivatedThisMonth: payload.deactivatedThisMonth,
          timezone: payload.timezone,
        },
      });

      if (!data) {
        throw new Error("Failed to fetch mentorships");
      }

      return data;
    },
  });
};

export interface DashboardStats {
  teenEngagement: number;
  teenMessagesSent: number;
  averageResponseTime: number;
  scheduledAverageResponseTime: number;
  m1Retention: number;
  averageRating: number;
  totalActiveTeens: number;
  totalEngagedTeens: number;
  totalBillableTeens: number;
}

export const useDashboardStats = (
  month?: number,
  groups?: string[],
  last7Days?: boolean,
) => {
  return useQuery<DashboardStats>({
    queryKey: ["dashboard-stats", month, groups, last7Days],
    queryFn: async () => {
      const response = await fetchApi<DashboardStats>({
        url: "v2/admin/stats",
        method: "GET",
        params: {
          month: month === -1 ? undefined : month,
          groups: groups?.includes("all") ? undefined : groups,
          last7Days,
        },
      });

      if (!response.data) {
        throw new Error("Failed to fetch dashboard stats");
      }

      return response.data;
    },
    staleTime: 60 * 1000 * 60, //1hr
  });
};
